import React, { useEffect, useRef, useState } from 'react';
import { Button, CustomInput, Row } from 'reactstrap';
import {
  productEditingModes,
  ProductImageStatus,
} from '../../constants/productApproval';
import productAPI from '../../services/API/product';
import { NotificationManager } from '../common/react-notifications';
import { ImageSelector } from './ImageSelector';
import { ProductImage } from './ProductImage';

export const ProductImagesView = ({
  productId,
  editingMode,
  onImagesChanged,
  isAllowAddImage,
  imageAddError,
  isRental,
}) => {
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(
    images.length > 0 ? images[0] : null
  );
  const primaryRefSet = useRef(false);

  const isMountedRef = useRef(true);
  const shouldSyncWithRemote =
    editingMode !== productEditingModes.ADD_NEW && !!productId;

  useEffect(() => {
    const shouldLoadImages =
      editingMode !== productEditingModes.ADD_NEW && !!productId;
    if (shouldLoadImages) loadProductImages();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (onImagesChanged) {
      console.log('why', images);
      onImagesChanged(images);
    }
  }, [images]);

  const loadProductImages = () => {
    setLoadingImages(true);

    productAPI
      .getProductImages(productId)
      .then((data) => {
        if (!isMountedRef.current) return;
        const imagesData = bringPrimaryToFirst(data);
        setImages(imagesData);
        setSelectedImage(imagesData[0]);
      })
      .catch(() => {
        NotificationManager.error({
          message: 'Something went wrong! Please try again!',
        });
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoadingImages(false);
      });
  };

  const handleDeleteImage = (id) => {
    let newImages = images.filter((img) => img.id !== id);
    if (newImages.length && !newImages[0].img_primary)
      newImages[0].img_primary = true;
    setImages(newImages);
    setSelectedImage(newImages[0]);

    if (shouldSyncWithRemote) {
      productAPI
        .deleteProductImage(id)
        .then((data) => {
          if (data.success) {
            NotificationManager.success({
              message: 'Image Removed',
            });
          } else {
            NotificationManager.error({
              message: data?.message || 'Something went wrong!',
            });
          }
        })
        .catch(() => {
          NotificationManager.error({
            message: 'Something went wrong! Please try again!',
          });
        });
    } else {
    }
  };

  const bringPrimaryToFirst = (imgs) =>
    imgs.sort((x, y) => y.img_primary - x.img_primary);

  const handleSetImagePrimary = (id) => {
    let newImages = images.map((img) => {
      img.img_primary = img.id === id;
      return img;
    });
    const imagesData = bringPrimaryToFirst(newImages);
    setImages(imagesData);
    setSelectedImage(imagesData[0]);

    if (shouldSyncWithRemote) {
      productAPI
        .setPrimaryImage({
          image_id: id,
          product_id: productId,
        })
        .then((data) => {
          if (data.success) {
            NotificationManager.success({
              message: 'Image Set Primary',
            });
          } else {
            NotificationManager.error({
              message: data?.message || 'Something went wrong!',
            });
          }
        })
        .catch(() => {
          NotificationManager.error({
            message: 'Something went wrong! Please try again!',
          });
        });
    }
  };

  const updateImageData = (id, update) => {
    setImages((oldImages) =>
      oldImages.map((oldImage) => {
        if (oldImage.id === id) {
          return {
            ...oldImage,
            ...update,
          };
        }
        return oldImage;
      })
    );
    setSelectedImage(images[0]);
  };

  const uploadImage = async (newImage) => {
    productAPI
      .uploadImage(newImage)
      .then(({ img_url }) => {
        let isPrimary = !primaryRefSet.current;
        if (isPrimary) primaryRefSet.current = true;
        console.log('This', {
          status: ProductImageStatus.UPLOADED,
          img_primary: isPrimary,
          url: img_url,
        });
        updateImageData(newImage.id, {
          status: ProductImageStatus.UPLOADED,
          img_primary: isPrimary,
          url: img_url,
        });
        return img_url;
      })
      .then((imageUrl) => {
        // add the image here
        if (shouldSyncWithRemote) {
          productAPI.addProductImages(productId, [imageUrl]);
        }
      })
      .catch((err) => {
        console.log('Error', err);
        updateImageData(newImage.id, {
          status: ProductImageStatus.UPLOAD_FAILED,
        });
      });
  };

  const handleAddImage = (imageFiles) => {
    let newImages = imageFiles.map((imgFile) => ({
      ...imgFile,
      img_url: imgFile.uri,
      status: ProductImageStatus.UPLOADING,
      progress: 0,
    }));
    const imagesData = [...images, ...newImages];
    setImages(imagesData);
    setSelectedImage(imagesData[0]);

    for (let newImage of newImages) {
      uploadImage(newImage);
    }
  };

  const imagesComponent =
    images.length > 0 ? (
      images.map((image, index) => (
        <ProductImage
          key={`image_${index}`}
          image={image}
          index={index}
          onSetPrimary={handleSetImagePrimary}
          onDeleteImage={handleDeleteImage}
          isAllowAddImage={isAllowAddImage}
        />
      ))
    ) : (
      <span style={{ fontSize: '1rem', fontFamily: 'Brown', fontWeight: 500 }}>
        No images uploaded
      </span>
    );

  const allowAddImage = editingMode !== productEditingModes.EDIT_EXISTING;

  return (
    <>
      {loadingImages ? (
        <div className='d-flex align-items-center justify-content-center h-100'>
          Loading...
        </div>
      ) : images.length > 0 && selectedImage ? (
        <div>
          <div
            style={{
              height: 380,
              width: '100%',
              border: 0,
              backgroundColor: '#F5E5D9',
            }}
          >
            <img
              src={selectedImage.img_url}
              style={{ height: '100%', width: '100%', objectFit: 'contain' }}
              alt={selectedImage.id}
            />
          </div>
          {images.length > 0 ? (
            <div
              className='d-flex align-items-center mt-2'
              style={{ flexWrap: 'wrap' }}
            >
              {images.map((i, index) => {
                return (
                  <div
                    style={{
                      height: 70,
                      width: 60,
                      border: 0,
                      marginRight: index === images.length - 1 ? 0 : 10,
                      backgroundColor: '#F5E5D9',
                      cursor: 'pointer',
                      marginBottom: 10,
                    }}
                    onClick={() => setSelectedImage(i)}
                  >
                    <img
                      src={i.img_url}
                      style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                      }}
                      alt={i.id}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : (
        <div className='d-flex align-items-center justify-content-center h-100'>
          No images found
        </div>
      )}
    </>
  );
};
